import { useTranslation } from 'react-i18next';
import { Icon, AlertTriangle } from '../../ui/Icon';
import type { BudgetStatusItem, Trip } from '../../../types';
import { tripPeople, useBudgetLabel, useBudgetRemainderText } from './budgetHelpers';

interface Props {
  status: BudgetStatusItem[];
  trip: Trip;
}

/** Banner listing limits that are over (red) or close (amber). Hidden when all ok. */
export function BudgetAlerts({ status, trip }: Props) {
  const { t } = useTranslation();
  const label = useBudgetLabel();
  const remainderText = useBudgetRemainderText();
  const people = tripPeople(trip);
  const currency = trip.budgetCurrency || 'EUR';

  const flagged = status
    .filter(s => s.state !== 'ok')
    .sort((a, b) => (a.state === 'over' ? 0 : 1) - (b.state === 'over' ? 0 : 1));

  if (flagged.length === 0) return null;

  const hasOver = flagged.some(s => s.state === 'over');

  return (
    <div className={`rounded-xl border px-4 py-3 ${
      hasOver
        ? 'border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30'
        : 'border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/30'
    }`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon as={AlertTriangle} size={15} className={hasOver ? 'text-red-500' : 'text-amber-500'} />
        <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
          {t('expenses.budget.alertsTitle')}
        </span>
      </div>
      <ul className="space-y-1">
        {flagged.map(s => {
          // For a DAILY cap, spell out that it's specific day(s) over it — not
          // that every day is — so "Por día" doesn't read as a blanket alert.
          const daysOver = s.scope === 'DAILY' && s.overDays && s.overDays.length > 0
            ? t('expenses.budget.daysOver', { count: s.overDays.length })
            : null;
          return (
            <li key={s.id} className="flex items-center justify-between gap-3 text-[13px]">
              <span className="text-slate-700 dark:text-slate-300 truncate">
                {label(s, people)}
                {daysOver && <span className="text-slate-400 dark:text-slate-500"> · {daysOver}</span>}
              </span>
              <span className={`font-mono tabular-nums shrink-0 ${
                s.state === 'over' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'
              }`}>
                {remainderText(s, currency)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
